import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";
import { Zap, AlertCircle, ArrowLeft, ShieldCheck } from "lucide-react";

export default function Verify2FA() {
  const { verify2FA } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();

  const challengeId = location.state?.challenge_id;

  const [digits, setDigits] = useState(Array(6).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  // Guard: if no challenge_id in state, this page was accessed directly — redirect to login
  useEffect(() => {
    if (!challengeId) {
      navigate("/login", { replace: true });
    }
  }, [challengeId, navigate]);

  // Auto-focus first box on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleDigitChange = (idx, val) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const newDigits = [...digits];
    newDigits[idx] = digit;
    setDigits(newDigits);
    if (digit && idx < 5) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace") {
      if (digits[idx]) {
        const newDigits = [...digits];
        newDigits[idx] = "";
        setDigits(newDigits);
      } else if (idx > 0) {
        inputRefs.current[idx - 1]?.focus();
      }
    }
    if (e.key === "ArrowLeft" && idx > 0) inputRefs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newDigits = Array(6).fill("");
    for (let i = 0; i < pasted.length; i++) newDigits[i] = pasted[i];
    setDigits(newDigits);
    const focusIdx = Math.min(pasted.length, 5);
    inputRefs.current[focusIdx]?.focus();
  };

  const code = digits.join("");
  const isComplete = code.length === 6;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isComplete || loading) return;
    setError("");
    setLoading(true);
    try {
      await verify2FA(challengeId, code);
      toast.success(`Access verified — Welcome to ${brand.productName}`);
      navigate("/home", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Verification failed. Please try again.";
      setError(msg);
      setDigits(Array(6).fill(""));
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  if (!challengeId) return null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F3F3F3] px-4">
      {/* Background grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

      <div className="relative w-full max-w-md">
        {/* Brand */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded bg-[#0176D3] flex items-center justify-center mb-4">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <h1 data-testid="brand-product-name" className="text-3xl font-bold text-[#181818] font-sans tracking-tight">{brand.productName}</h1>
          <p data-testid="brand-company-name" className="text-sm text-[#706E6B] mt-1 font-mono">AI Powered by {brand.companyName}</p>
        </div>

        {/* Card */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-8" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          {/* Icon + heading */}
          <div className="flex flex-col items-center mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#EEF4FF] border border-[#0176D3]/20 flex items-center justify-center mb-3">
              <ShieldCheck className="w-5 h-5 text-[#0176D3]" />
            </div>
            <h2 className="text-lg font-semibold text-[#181818] font-sans">Verify Your Access</h2>
            <p className="text-sm text-[#706E6B] mt-1 text-center">
              Enter the 6-digit verification code.
            </p>
          </div>

          <form data-testid={AUTH.twoFAForm} onSubmit={handleSubmit}>
            {/* 6-digit boxes */}
            <div className="flex items-center justify-center gap-2 mb-6" onPaste={handlePaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={el => (inputRefs.current[i] = el)}
                  data-testid={`verify-2fa-digit-${i}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={e => handleDigitChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  className={`w-11 h-13 sm:w-12 sm:h-14 text-center text-xl font-bold rounded border transition-all duration-150 bg-white text-[#181818] outline-none
                    ${d ? "border-[#0176D3]" : "border-[#C9C9C9]"}
                    focus:border-[#1B96FF] focus:ring-1 focus:ring-[#1B96FF]`}
                  style={{ height: "3.25rem" }}
                />
              ))}
            </div>

            {error && (
              <div
                data-testid={AUTH.twoFAError}
                className="flex items-start gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-400 text-sm mb-4"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              data-testid={AUTH.twoFASubmit}
              type="submit"
              disabled={!isComplete || loading}
              className="w-full py-3 rounded bg-[#0176D3] text-white font-semibold text-sm hover:bg-[#014486] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? "Verifying..." : "Verify & Continue"}
            </button>
          </form>

          {/* Back link */}
          <button
            data-testid="verify-2fa-back-btn"
            onClick={() => navigate("/login")}
            className="w-full mt-4 flex items-center justify-center gap-2 text-sm text-[#706E6B] hover:text-[#444444] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>
        </div>

        {/* Hint */}
        <p className="text-center text-xs font-mono text-[#444444] mt-4">
          Code expires in 10 minutes · Maximum 5 attempts
        </p>
      </div>
    </div>
  );
}
